/**
 * Finds the text node at a specific character offset within an HTML element.
 * Traverses child nodes accounting for locked blocks and special formatting to locate the text node and its internal offset.
 *
 * @param el - The HTML element to search for text nodes in
 * @param offset - The character offset position to locate within the element's text content
 * @returns Object with the text node and internal offset, or null if not found
 */
export const getTextNodeAtOffset = (
  el: Element,
  offset: number,
): { node: Text; offset: number } | null => {
  let currentPos = 0;
  if (el)
    for (let _i = 0; _i < el.childNodes.length && currentPos <= offset; _i++) {
      const currentNode = el.childNodes[_i] as Node;
      /* istanbul ignore else */
      if (
        currentPos + (currentNode?.textContent?.replaceAll(/[\u2009-\u200f]/g, "") ?? "").length <
        offset
      ) {
        currentPos += (currentNode?.textContent?.replaceAll(/[\u2009-\u200f]/g, "") ?? "").length;
      }
      // Check for locked block
      else if (
        currentNode instanceof HTMLSpanElement &&
        currentNode.classList.contains("is-locked") &&
        offset !== 0
      ) {
        const nextSibling = currentNode.nextSibling;
        if (nextSibling instanceof Text) {
          return { node: nextSibling, offset: 0 };
        } else {
          const newTextNode = document.createTextNode("");
          el.insertBefore(newTextNode, nextSibling);
          return { node: newTextNode, offset: 0 };
        }
      } else if (currentNode instanceof Element) {
        return getTextNodeAtOffset(currentNode, offset - currentPos);
      } else if (currentNode instanceof Text) {
        /* istanbul ignore else */
        return { node: currentNode, offset: offset - currentPos };
      }
    }
  return null;
};
